/* eslint-disable @typescript-eslint/naming-convention */
export enum MostRecentActivityStatus {
  inProgress = 'inProgress',
  newItem = 'newItem',
  designDone = 'designDone',
  licensingReview = 'licensingReview',
  approved = 'approved',
  needChanges = 'needChanges',
  none = 'none',
  inStudio = 'inStudio',
  inProduction = 'inProduction',
  outForDelivery = 'outForDelivery',
  delivered = 'delivered',
  orderLive = 'orderLive',
  permanentStore = 'permanentStore',
  failedGroupOrder = 'failedGroupOrder',
}

/** Store is open for orders (live or permanent preview). */
export const isLiveStoreOrderStatus = (status: string): boolean =>
  status === MostRecentActivityStatus.orderLive ||
  status === MostRecentActivityStatus.permanentStore;

export const isPermanentStoreOrderStatus = (status: string): boolean =>
  status === MostRecentActivityStatus.permanentStore;

export const normalizeStoreType = (storeType?: string): string =>
  storeType?.trim().toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-') ?? '';

export interface IPermanentStoreOrderHints {
  /** True when the order is a new group-order / store (NGO). */
  isNewGroupOrder?: boolean;
  /**
   * Close date for classic group orders. Permanent stores intentionally have no
   * close date; v3 CP order detail often still omits `store_type`, so NGO +
   * missing close date is the reliable FE signal.
   */
  groupOrderDate?: string | null;
}

export const isPermanentStoreOrder = (
  status: string,
  storeType?: string,
  hints?: IPermanentStoreOrderHints
): boolean => {
  if (isPermanentStoreOrderStatus(status)) {
    return true;
  }

  const normalizedStoreType = normalizeStoreType(storeType);
  if (
    normalizedStoreType === 'permanent' ||
    normalizedStoreType === 'permanent-store' ||
    normalizedStoreType.includes('permanent')
  ) {
    return true;
  }

  // Fallback when CP omits store_type: permanent stores are NGO with no close date.
  // Do not apply this when store_type is present and explicitly non-permanent.
  if (
    !normalizedStoreType &&
    hints?.isNewGroupOrder &&
    (hints.groupOrderDate == null || hints.groupOrderDate === '')
  ) {
    return true;
  }

  return false;
};

export const mostRecentActivityStatusMap: Record<string, string> = {
  'In Progress': MostRecentActivityStatus.inProgress,
  'Design Done': MostRecentActivityStatus.designDone,
  Approved: MostRecentActivityStatus.approved,
  'Need Changes': MostRecentActivityStatus.needChanges,
  'New Item': MostRecentActivityStatus.newItem,
  'Licensing Review': MostRecentActivityStatus.licensingReview,
  'In Studio': MostRecentActivityStatus.inStudio,
  'In Production': MostRecentActivityStatus.inProduction,
  'Out for Delivery': MostRecentActivityStatus.outForDelivery,
  Delivered: MostRecentActivityStatus.delivered,
  'Order Live': MostRecentActivityStatus.orderLive,
  'Permanent Store': MostRecentActivityStatus.permanentStore,
  'Failed Group Order': MostRecentActivityStatus.failedGroupOrder,
};
